<div class="terms">
  <nav class="legal-nav">
    <a [routerLink]="'/terms-of-use'">Terms of Use</a>
    <a [routerLink]="'/privacy-policy'" class="active">Privacy Policy</a>
    <a [routerLink]="'/subscription-terms'">Subscription Terms</a>
  </nav>

  <h1 class="center"><strong>JustTeach Privacy Policy</strong></h1>
  <p class="center last-updated">Last Updated: December 2025</p>

  <p class="intro">
    This Privacy Policy explains how Justspeak ltd ("JustTeach," "we," "us," or "our") collects,
    uses, and discloses information about our users, including teachers, school administrators, and
    students, through our website at justteach.ai and our AI-powered teacher assistant services
    (collectively, the "Service").
  </p>
  <p class="intro">
    We are committed to protecting your privacy and the privacy of the students who use our Service
    under the direction of a teacher or school administrator. We do not sell or rent personal
    information to third parties.
  </p>
  <p class="intro">
    Justspeak Ltd, registered at Kypranoros, 13, EVI BUILDING, 2nd floor, Flat/Office 201 1061,
    Nicosia, Cyprus, will be the controller of your personal data.
  </p>

  <section>
    <h2>1. Applicability of This Policy</h2>
    <p>This Policy applies to two primary groups of users:</p>
    <ul>
      <li>
        <strong>Teachers and School Administrators (Adult Users):</strong> Users who create an
        account, pay for the Service, and use the platform for lesson planning and classroom
        management.
      </li>
      <li>
        <strong>Students (Child Users):</strong> Users whose information is provided by a teacher or
        who access the Service under the direct supervision and control of their teacher or school.
      </li>
    </ul>
  </section>

  <section>
    <h2>2. Information We Collect</h2>
    <p>
      We collect information directly from you, automatically through your use of our Service, and
      from third-party sources.
    </p>

    <h3>A. Information Collected from Teachers and Administrators</h3>
    <p>
      We collect the following types of information when an adult user creates an account or uses
      the Service:
    </p>
    <ul>
      <li>
        <strong>Account Information:</strong> Name, email address, school or district name, and
        password.
      </li>
      <li>
        <strong>Payment Information:</strong> If you subscribe to a paid plan, we collect payment
        details (handled by a third-party payment processor, who is the sole recipient of your
        credit card details).
      </li>
      <li>
        <strong>User-Generated Content:</strong> Lesson plans, teaching materials, quizzes, class
        names, and any text inputs (prompts) submitted to the AI assistant.
      </li>
      <li>
        <strong>Usage and Technical Data:</strong> Log data, IP address, device type, operating
        system, browser type, and timestamps related to your use of the Service.
      </li>
    </ul>

    <h3>B. Information Collected about Students (Child Users)</h3>
    <p>
      Our Service is primarily designed for use by teachers and school administrators. We may
      collect limited personal information about students only when a school or teacher provides it
      to us for an educational purpose, such as:
    </p>
    <ul>
      <li>
        <strong>Student Identifiers (Optional):</strong> Student names or internal school IDs (used
        only to organize class lists and personalize learning materials).
      </li>
      <li>
        <strong>Classroom Data:</strong> Student performance, progress, and activity data related to
        quizzes or assignments created and administered through JustTeach by the teacher.
      </li>
      <li>
        <strong>AI-Generated Content:</strong> Personalized activities, questions, or feedback
        generated by the Service based on student progress, which is shared only with the specific
        teacher and student.
      </li>
    </ul>
    <p>
      We do not solicit, and teachers should not provide, sensitive student information, such as
      home address, phone number, or date of birth.
    </p>
  </section>

  <section>
    <h2>3. How We Use the Information</h2>
    <p>
      We use the information we collect, including Student Data, solely for the following
      educational purposes:
    </p>
    <ul>
      <li>
        <strong>To Provide and Maintain the Service:</strong> To deliver the core functionality of
        the AI teacher assistant, including generating, saving, and organizing lesson plans,
        activities, and quizzes.
      </li>
      <li>
        <strong>To Personalize the Student Experience:</strong> To allow teachers to track student
        progress and generate differentiated materials tailored to individual student needs, based
        on the input data.
      </li>
      <li>
        <strong>Communication:</strong> To send teachers and administrators service-related updates,
        security alerts, and administrative messages.
      </li>
      <li>
        <strong>To Improve the Service (Aggregated Data Only):</strong> To analyze user behavior and
        patterns to improve the quality of our AI models and platform features. This analysis is
        performed using de-identified or aggregated data that does not personally identify any user.
      </li>
      <li>
        <strong>Security and Compliance:</strong> To detect, prevent, and respond to potential
        fraud, abuse, or unauthorized access.
      </li>
    </ul>
  </section>

  <section>
    <h2>4. How We Share and Disclose Information</h2>
    <p>
      We do not rent or sell your Personal Information or Student Data. We share information only in
      the following limited circumstances:
    </p>

    <table class="data-table">
      <tr>
        <th>Category</th>
        <th>Description</th>
      </tr>
      <tr>
        <td>With School or District</td>
        <td>
          We may share a teacher's activity data with the relevant school or district administrator
          upon their request, as needed to manage and audit the school's use of the Service.
        </td>
      </tr>
      <tr>
        <td>Service Providers</td>
        <td>
          We use third-party vendors (e.g., hosting providers, security services, data analytics) to
          perform services on our behalf. These parties are contractually obligated to protect the
          data and use it only for the purpose of providing the contracted services.
        </td>
      </tr>
      <tr>
        <td>Legal Requirements</td>
        <td>
          We may disclose information if required to do so by law, court order, or subpoena, or if
          we believe that such action is necessary to comply with a legal obligation.
        </td>
      </tr>
      <tr>
        <td>Business Transfers</td>
        <td>
          In the event of a merger, acquisition, or sale of all or a portion of our assets, user
          information may be transferred to the successor entity, provided they are bound by the
          same privacy protections.
        </td>
      </tr>
    </table>

    <p>
      Crucially, all student-specific data is only accessible to the teacher(s) and administrators
      affiliated with the student's account.
    </p>
  </section>

  <section>
    <h2>5. Compliance with COPPA and FERPA</h2>
    <p>JustTeach is committed to compliance with all applicable data privacy laws, including:</p>
    <ul>
      <li>
        <strong>Children's Online Privacy Protection Act (COPPA):</strong> We rely on the school or
        teacher to provide the necessary consent for the collection of student information, as
        permitted by COPPA's school official exception. We only collect the minimal personal
        information necessary for educational purposes and do not use student information for
        targeted advertising.
      </li>
      <li>
        <strong>Family Educational Rights and Privacy Act (FERPA):</strong> JustTeach acts as a
        "School Official" with a legitimate educational interest in the Student Data provided by the
        school or teacher. We will not disclose Student Data to third parties except as necessary to
        provide the Service, as authorized by the school, or as required by law.
      </li>
    </ul>
    <p>
      The school or district maintains control over and responsibility for any Student Data
      submitted to the Service.
    </p>
  </section>

  <section>
    <h2>6. Your Rights and Choices</h2>

    <h3>A. Teacher and Administrator Rights</h3>
    <ul>
      <li>
        <strong>Access and Correction:</strong> You may access and update your account information
        directly through your JustTeach account settings.
      </li>
      <li>
        <strong>Account Deletion:</strong> You can request the deletion of your account and all
        associated data by contacting us at the email below.
      </li>
      <li>
        <strong>Marketing Opt-Out:</strong> You can opt out of receiving promotional emails from us
        by following the unsubscribe link in those emails.
      </li>
    </ul>

    <h3>B. School and Student Data Rights</h3>
    <p>
      Schools and districts have the right to request access, modification, or deletion of Student
      Data that is under their control. Parents/Guardians of students should direct any requests
      regarding their child's personal information to the child's school or district. We will
      coordinate with the school to fulfill these requests as required by law.
    </p>
  </section>

  <section>
    <h2>7. Data Security</h2>
    <p>
      We implement and maintain reasonable administrative, technical, and physical security measures
      to protect the personal information we collect and store, both during transmission and once
      received. While we strive to protect your data, no security system is impenetrable, and we
      cannot guarantee the absolute security of our systems.
    </p>
  </section>

  <section>
    <h2>8. International Users</h2>
    <p>
      The Service is hosted in the United States and is intended for users in the United States. If
      you access the Service from the European Union or any other region with laws governing data
      collection and use that may differ from U.S. law, please note that you are transferring your
      personal information to the United States. By providing your information, you consent to the
      transfer, storage, and processing of your data in the U.S.
    </p>
  </section>

  <section>
    <h2>9. Changes to this Privacy Policy</h2>
    <p>
      We may update this Policy from time to time. If we make material changes to how we collect or
      use Student Data, we will provide prior notice to the relevant school or teacher and update
      the "Last Updated" date at the top of this Policy. We encourage you to review this Policy
      periodically.
    </p>
  </section>

  <section>
    <h2>10. Contact Us</h2>
    <p>
      If you have any questions or concerns about this Privacy Policy or our data practices, please
      contact us at:
    </p>
    <div class="contact-info">
      <p><strong>Justspeak ltd</strong></p>
      <p>
        Email:
        <a href="mailto:justteachsupport@justteach.ai">justteachsupport&#64;justteach.ai</a>
      </p>
      <p>Website: <a href="https://justteach.ai">justteach.ai</a></p>
    </div>
  </section>
</div>

